// File: frontend/src/components/marketing/partners/PartnersTiers.js
//
// The tier showcase and the milestone rewards on /ambassador.
//
// Every threshold, label and reward amount comes from GET
// /api/partners/program/public. Nothing here is a constant, so the page can
// never advertise a threshold or a reward the engine does not use.
//
// An unconfigured reward (null) reads "Amount to be announced". Never UGX 0,
// never a placeholder figure: on the one Ambassador surface a stranger reads as
// an offer, an invented amount is a promise nobody made.

import React, { useRef, useState } from "react";
import { Box, Stack, Typography, Skeleton, alpha, useTheme } from "@mui/material";
import { keyframes } from "@emotion/react";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

import { gsap, useGSAP, DUR, EASE, MQ } from "../../../utils/gsapCore";
import { ugx } from "../../../pages/partner/partnerShared";
import TierBadgeArt from "../visuals/PartnerTierBadgeVisual";
import { Section, SectionHeading, Surface } from "./PartnersPrimitives";
import { useReveal } from "./partnersMotion";

export const TO_BE_ANNOUNCED = "Amount to be announced";

/** "10 to 49 active clients", or "250+ active clients" for the top tier. */
export function tierRange(tiers, i) {
  const t = tiers[i];
  const next = tiers[i + 1];
  if (!next) return `${t.threshold}+ active clients`;
  const upper = next.threshold - 1;
  return upper > t.threshold ? `${t.threshold} to ${upper} active clients` : `${t.threshold} active clients`;
}

export function rewardText(t) {
  if (t.threshold === 0) return "Starting tier, no milestone reward";
  return t.reward_amount > 0 ? ugx(t.reward_amount) : TO_BE_ANNOUNCED;
}

const sweep = keyframes`
  from { transform: translateX(-100%); }
  to   { transform: translateX(100%); }
`;

const BUTTON_RESET = {
  appearance: "none", font: "inherit", color: "inherit", m: 0, cursor: "pointer",
  bgcolor: "transparent",
};

/** Loading and failure states: never a guessed figure. */
function DataState({ status, what }) {
  if (status === "error") {
    return (
      <Surface strong sx={{ p: { xs: 3, md: 4 }, textAlign: "center" }}>
        <Typography component="p" sx={{ fontWeight: 700, color: "text.primary" }}>
          {what} are unavailable right now.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.75, color: "text.secondary" }}>
          Please refresh the page to try again.
        </Typography>
      </Surface>
    );
  }
  return (
    <Box aria-busy="true">
      <Typography component="p" sx={{ position: "absolute", width: "1px", height: "1px", overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>
        Loading {what.toLowerCase()}
      </Typography>
      <Skeleton variant="rounded" height={260} sx={{ borderRadius: 3 }} />
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TIER SHOWCASE
// ═══════════════════════════════════════════════════════════════════════════

export function TierShowcase({ tiers, status, activeWindowDays, graceWindowDays, ratePct }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const ready = status === "ready" && tiers.length > 0;
  const failed = status === "error" || (status === "ready" && tiers.length === 0);

  const root = useReveal([ready, tiers.length]);
  const panelRef = useRef(null);
  const tabRefs = useRef([]);
  const [sel, setSel] = useState(0);

  const { contextSafe } = useGSAP({ scope: root, dependencies: [] });

  const choose = contextSafe((i, focus = false) => {
    if (focus) tabRefs.current[i]?.focus();
    if (i === sel) return;
    setSel(i);
    const reduce = typeof window !== "undefined" && window.matchMedia?.(MQ.reduced)?.matches;
    if (!reduce && panelRef.current) {
      // A short crossfade from a still-readable state, handed back to CSS.
      gsap.fromTo(
        panelRef.current,
        { opacity: 0.35, y: 6 },
        { opacity: 1, y: 0, duration: DUR.fast + 0.1, ease: EASE.out, overwrite: "auto", clearProps: "opacity,transform" }
      );
    }
  });

  const onKeyDown = (e) => {
    const n = tiers.length;
    const map = { ArrowRight: sel + 1, ArrowDown: sel + 1, ArrowLeft: sel - 1, ArrowUp: sel - 1, Home: 0, End: n - 1 };
    if (!(e.key in map)) return;
    e.preventDefault();
    choose((map[e.key] + n) % n, true);
  };

  const t = ready ? tiers[Math.min(sel, tiers.length - 1)] : null;
  const next = ready ? tiers[sel + 1] : null;
  const rateText = ratePct != null ? `${ratePct}%` : null;

  const subtitle = activeWindowDays
    ? `Your tier follows how many of your referred businesses are active, meaning they generated qualifying PesaFii revenue in the last ${activeWindowDays} days. Tiers unlock milestone rewards. They never change your rate.`
    : "Your tier follows how many of your referred businesses are actively generating qualifying PesaFii revenue. Tiers unlock milestone rewards. They never change your rate.";

  return (
    <Section ref={root} id="tiers" labelledBy="amb-tiers-title">
      <SectionHeading
        id="amb-tiers-title"
        eyebrow="Ambassador tiers"
        title="Climb the tiers. Keep the same rate."
        subtitle={subtitle}
      />

      {!ready ? (
        <DataState status={failed ? "error" : "loading"} what="Tier details" />
      ) : (
        <Box data-reveal>
          {/* ── Selector ─────────────────────────────────────────────────── */}
          <Box
            role="tablist"
            aria-label="Ambassador tiers"
            onKeyDown={onKeyDown}
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${tiers.length}, minmax(0, 1fr))`,
              gap: { xs: 0.5, sm: 1.5 },
              mb: { xs: 2.5, md: 3.5 },
              maxWidth: 820,
              mx: "auto",
            }}
          >
            {tiers.map((tier, i) => {
              const on = i === sel;
              return (
                <Box
                  key={tier.key}
                  component="button"
                  type="button"
                  role="tab"
                  id={`amb-tier-tab-${tier.key}`}
                  aria-selected={on}
                  aria-controls="amb-tier-panel"
                  tabIndex={on ? 0 : -1}
                  ref={(node) => { tabRefs.current[i] = node; }}
                  onClick={() => choose(i)}
                  sx={{
                    ...BUTTON_RESET,
                    position: "relative",
                    overflow: "hidden",
                    minWidth: 0,
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75,
                    py: { xs: 1.25, sm: 1.75 }, px: 0.5,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: on ? "var(--amb-accent-line)" : "transparent",
                    bgcolor: on ? "var(--amb-surface-strong)" : "transparent",
                    boxShadow: on ? `0 12px 28px ${alpha(theme.palette.common.black, dark ? 0.4 : 0.08)}` : "none",
                    transition: "background-color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
                    "&:hover": { bgcolor: on ? "var(--amb-surface-strong)" : "var(--amb-accent-soft)" },
                    "&:hover .amb-medal": { transform: "translateY(-3px)" },
                    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                  }}
                >
                  <Box
                    className="amb-medal"
                    sx={{
                      transform: on ? "translateY(-3px)" : "none",
                      transition: "transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)",
                      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                    }}
                  >
                    <TierBadgeArt tier={tier.key} size={{ xs: 40, sm: 58, md: 70 }} label={null} selected={on} />
                  </Box>
                  <Box component="span" sx={{ fontSize: { xs: 12, sm: 14 }, fontWeight: on ? 800 : 600, color: on ? "text.primary" : "text.secondary", lineHeight: 1.2 }}>
                    {tier.label}
                  </Box>
                  <Box component="span" sx={{ display: { xs: "none", sm: "block" }, fontSize: 12, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                    {tier.threshold === 0 ? "Start" : `${tier.threshold}+`}
                  </Box>
                  {on && (
                    <Box
                      aria-hidden="true"
                      sx={{
                        position: "absolute", inset: 0, pointerEvents: "none",
                        background: `linear-gradient(105deg, transparent 35%, ${alpha(theme.palette.common.white, dark ? 0.08 : 0.55)} 50%, transparent 65%)`,
                        animation: `${sweep} 0.9s ease-out both`,
                        "@media (prefers-reduced-motion: reduce)": { display: "none" },
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>

          {/* ── Detail ───────────────────────────────────────────────────── */}
          <Box
            ref={panelRef}
            id="amb-tier-panel"
            role="tabpanel"
            aria-labelledby={`amb-tier-tab-${t.key}`}
            sx={{
              maxWidth: 900, mx: "auto",
              borderRadius: 4, border: "1px solid", borderColor: "var(--amb-line)",
              bgcolor: "var(--amb-surface-strong)",
              p: { xs: 2.5, sm: 3.5, md: 4.5 },
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "auto minmax(0, 1fr)" },
              gap: { xs: 2.5, sm: 4, md: 5 },
              alignItems: "center",
            }}
          >
            <Box sx={{ justifySelf: { xs: "center", sm: "start" } }}>
              <TierBadgeArt tier={t.key} size={{ xs: 104, md: 140 }} label={`${t.label} tier`} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" component="p" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                Tier {sel + 1} of {tiers.length}
              </Typography>
              <Typography variant="h3" component="h3" sx={{ color: "text.primary", fontWeight: 800, fontSize: { xs: "1.6rem", md: "1.95rem" }, lineHeight: 1.15 }}>
                {t.label}
              </Typography>
              <Typography component="p" sx={{ mt: 0.75, color: "primary.main", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                {tierRange(tiers, sel)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1.5, color: "text.secondary", maxWidth: 560 }}>
                {t.threshold === 0
                  ? "Where every Ambassador starts. You get your referral link, QR code and Ambassador dashboard from day one."
                  : `Reach ${t.threshold} active clients to hold ${t.label}. ${activeWindowDays
                    ? `A client counts as active when it has generated qualifying PesaFii revenue in the last ${activeWindowDays} days.`
                    : "A client counts as active when it is generating qualifying PesaFii revenue."}`}
              </Typography>

              <Box sx={{ mt: 2.5, display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.25 }}>
                <PanelFact label="Milestone reward" value={rewardText(t)} strong={t.reward_amount > 0} />
                <PanelFact label="Commission rate" value={rateText ? `${rateText}, same at every tier` : "Same at every tier"} />
              </Box>

              {/* Where this tier sits on the ladder. */}
              <Box aria-hidden="true" sx={{ mt: 3, display: "flex", gap: 0.5 }}>
                {tiers.map((x, j) => (
                  <Box
                    key={x.key}
                    sx={{
                      flex: 1, height: 6, borderRadius: 3,
                      bgcolor: j <= sel ? "primary.main" : "var(--amb-track)",
                      transition: "background-color 0.35s ease",
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" component="p" sx={{ mt: 1, color: "text.secondary" }}>
                {next ? `Next: ${next.label} at ${next.threshold} active clients` : "The top tier"}
              </Typography>
            </Box>
          </Box>

          {graceWindowDays && (
            <Typography variant="body2" sx={{ mt: 2.5, textAlign: "center", color: "text.secondary" }}>
              You keep a tier for {graceWindowDays} days after dropping below its threshold.
            </Typography>
          )}
        </Box>
      )}
    </Section>
  );
}

function PanelFact({ label, value, strong }) {
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "var(--amb-track)" }}>
      <Typography component="p" sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary" }}>{label}</Typography>
      <Typography component="p" sx={{ mt: 0.25, fontWeight: 700, color: strong ? "primary.main" : "text.primary", fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Typography>
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MILESTONE REWARDS
// ═══════════════════════════════════════════════════════════════════════════

export function MilestoneRewards({ tiers, status, graceWindowDays }) {
  const ready = status === "ready" && tiers.length > 0;
  const failed = status === "error" || (status === "ready" && tiers.length === 0);
  const root = useReveal([ready, tiers.length]);

  const rewardTiers = tiers.filter((t) => t.threshold > 0);
  const n = Math.max(rewardTiers.length, 1);
  const pending = rewardTiers.some((t) => !(t.reward_amount > 0));

  const rules = [
    "Paid once, the first time you reach each tier.",
    "Yours to keep. If your active client count drops later, the reward is not taken back.",
    "Reaching the same tier again does not pay it twice.",
    pending && "Reach a tier before its amount is announced and your milestone is still recorded. The reward is paid once the amount is set.",
    graceWindowDays && `You keep a tier for ${graceWindowDays} days after dropping below its threshold.`,
  ].filter(Boolean);

  return (
    <Section ref={root} id="rewards" labelledBy="amb-rewards-title">
      <SectionHeading
        id="amb-rewards-title"
        eyebrow="Milestone rewards"
        title="Reach a tier. Earn a one-time reward."
        subtitle="The first time you reach each tier, you receive a cash milestone reward on top of your recurring commission."
      />

      {!ready ? (
        <DataState status={failed ? "error" : "loading"} what="Milestone details" />
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 7fr) minmax(0, 5fr)" },
            gap: { xs: 3, md: 4 },
            alignItems: "center",
          }}
        >
          <Surface data-reveal strong sx={{ p: { xs: 2, sm: 3.5 }, borderRadius: 4 }}>
            <Box
              component="ol"
              aria-label="Milestone rewards by tier"
              sx={{
                listStyle: "none", m: 0, p: 0, position: "relative",
                display: "grid",
                gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: `repeat(${n}, minmax(0, 1fr))` },
                gap: { xs: 0, sm: 1.5 },
              }}
            >
              {/* The path the milestones sit on. */}
              <Box
                aria-hidden="true"
                sx={{
                  position: "absolute",
                  bgcolor: "var(--amb-accent-line)",
                  borderRadius: 1,
                  top: { xs: 40, sm: 28 },
                  bottom: { xs: 40, sm: "auto" },
                  left: { xs: 27, sm: `${50 / n}%` },
                  right: { xs: "auto", sm: `${50 / n}%` },
                  width: { xs: 2, sm: "auto" },
                  height: { xs: "auto", sm: 2 },
                }}
              />
              {rewardTiers.map((t) => {
                const configured = t.reward_amount > 0;
                return (
                  <Box
                    component="li"
                    key={t.key}
                    sx={{
                      position: "relative",
                      display: "grid",
                      gridTemplateColumns: { xs: "56px minmax(0, 1fr)", sm: "minmax(0, 1fr)" },
                      justifyItems: { xs: "start", sm: "center" },
                      alignItems: "center",
                      columnGap: 2,
                      rowGap: 1,
                      py: { xs: 1.5, sm: 0 },
                      textAlign: { xs: "left", sm: "center" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 56, height: 56, borderRadius: "50%", zIndex: 1,
                        display: "grid", placeItems: "center",
                        bgcolor: "var(--amb-surface-strong)",
                      }}
                    >
                      <TierBadgeArt tier={t.key} size={40} label={null} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography component="p" sx={{ fontWeight: 800, color: "text.primary", lineHeight: 1.25 }}>{t.label}</Typography>
                      <Typography component="p" sx={{ fontSize: 13, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                        {t.threshold} active clients
                      </Typography>
                      <Typography
                        component="p"
                        sx={{
                          mt: 0.5, fontSize: 14, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                          color: configured ? "primary.main" : "text.secondary",
                        }}
                      >
                        {configured ? ugx(t.reward_amount) : TO_BE_ANNOUNCED}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Surface>

          <Stack component="ul" spacing={1.75} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {rules.map((line) => (
              <Box component="li" key={line} data-reveal sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                <CheckCircleOutlineRoundedIcon aria-hidden="true" sx={{ fontSize: 20, mt: "2px", color: "primary.main", flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>{line}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Section>
  );
}
